import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Avatar,
  Chip,
  Grid,
  Pagination,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { WatchLater } from '@mui/icons-material';
import { ExpierienceYears, ServiceOffer } from '../../common/const';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLoading } from '../../features/authSlice';
import { showNotification } from '../../common/components/Notification/ToastifyNotification';
import { confirmBooking, getEventByInvite } from '../../api/sessionApi';
import CommonBookSession from '../../common/components/CommonBookSession';
import theme from '../../theme';
import { inviteCounsellor } from '../../api/mentorApi';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const BookSessionButton = styled(Button)({
  backgroundColor: '#FBB424',
  color: '#1f2937',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  height: '35px',
  '&:hover': {
    backgroundColor: '#F59E0B',
  },
});

const ViewProfileButton = styled(Button)({
  color: '#1E3A8A',
  borderColor: '#1E3A8A',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  height: '35px',
  '&:hover': {
    borderColor: '#1E3A8A',
    backgroundColor: '#F0F4FF',
  },
});

const StyledChip = styled(Chip)({
  backgroundColor: '#F3F4F6',
  color: '#4B5563',
  fontSize: '12px',
  fontWeight: 500,
  borderRadius: '6px',
  height: '28px',
});

const StyledPagination = styled(Pagination)({
  '& .MuiPaginationItem-root': {
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    color: '#374151',
    fontWeight: 500,
    minWidth: '40px',
    height: '40px',
    '&:hover': {
      borderColor: '#1E3A8A',
      color: '#1E3A8A',
      backgroundColor: '#F0F4FF',
    },
    '&.Mui-selected': {
      backgroundColor: '#1E3A8A',
      borderColor: '#1E3A8A',
      color: 'white',
      '&:hover': {
        backgroundColor: '#1E40AF',
      },
    },
  },
  '& .MuiPaginationItem-previousNext': {
    fontSize: '20px',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '540px',
    width: '100%',
  },
});

const MentorListing = ({
  currentPage,
  setCurrentPage,
  totalPages,
  itemsPerPage,
  mentors,
  details
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [viewProfileDialog, setViewProfileDialog] = useState(false);
  const [bookSessionDialog, setBookSessionDialog] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [sessionNote, setSessionNote] = useState('');
  const [imageUrls, setImageUrls] = useState({});
  const [open, setOpen] = useState(false);
  const [bookingUrl, setBookingUrl] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [eventData, setEventData] = useState(null);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewProfile = (mentor) => {
    setSelectedMentor(mentor);
    setViewProfileDialog(true);
  };

  const handleConfirmBooking = async () => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const data = {
        "mentor_profile_id": selectedMentor?.mentor_profile_id,
        "student_id": user?.student_id,
        "service_offers": [ServiceOffer.counsellor],
        "notes": sessionNote,
        "status": "CONFIRMED",
        "invitee_email": eventData.invitee_email,
        "calendly_event_uri": eventData.calendly_event_uri,
        "calendly_invitee_uri": eventData.calendly_invitee_uri
      }
      const response = await confirmBooking(data);
      if (response) {
        dispatch(setLoading({
          loading: false
        }));
        setBookSessionDialog(false);
        setConfirmationDialog(true);
        setSessionNote(null);
        setBookingUrl(null);
        setInitialData(null);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  };

  const handleCloseAll = () => {
    navigate(`/invite-counsellor`);
    setViewProfileDialog(false);
    setBookSessionDialog(false);
    setConfirmationDialog(false);
    setSelectedMentor(null);
    setSessionNote('');
    setEventData(null);
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, mentors?.length);

  // Function to load image as blob
  const loadImageAsBlob = async (avatarPath, userId) => {
    try {
      const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${avatarPath}&download=false`;
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      setImageUrls(prev => ({
        ...prev,
        [userId]: objectUrl
      }));
    } catch (error) {
      console.error('Error loading image:', error);
    }
  };

  // Load all images when mentors are set
  useEffect(() => {
    if (mentors?.length > 0) {
      mentors?.map(mentor => {
        if (mentor.user?.avatar && !imageUrls[mentor.user_id]) {
          loadImageAsBlob(mentor.user.avatar, mentor.user_id);
        }
      });
    }
  }, [mentors]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageUrls)?.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);


  const formatDateRange = (startDate, endDate, currentJob) => {
    const start = moment(startDate).format('MMM yyyy');
    const end = currentJob ? 'Present' : (endDate ? moment(endDate).format('MMM yyyy') : '');
    return `${start} - ${end}`;
  };

  const getEventData = async () => {
    try {
      dispatch(setLoading({
        loading: true
      }));
      const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
      setEventData(response.data);
      setBookSessionDialog(true);
      dispatch(setLoading({
        loading: false
      }));
    } catch (err) {
      const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
      showNotification(errorMessage, 'error');
      dispatch(setLoading({
        loading: false
      }));
    }
  };

  useEffect(() => {
    if (initialData !== null) {
      dispatch(setLoading({
        loading: true
      }));
      setTimeout(() => {
        getEventData();
      }, 5000);
    }
  }, [initialData]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Mentor Grid */}
      <CommonBookSession
        bookingUrl={bookingUrl}
        open={open}
        setOpen={setOpen}
        studentName={`${user?.first_name} ${user?.last_name}`}
        email={user?.email}
        handleClose={(data) => {
          setOpen(false);
          setInitialData(data);
          setViewProfileDialog(false);
        }}
      />
      {mentors?.length > 0 ? (
        <Box sx={{ height: '60vh', overflow: 'auto' }}>
          <Grid container spacing={2} sx={{ mb: 5 }}>
            {mentors?.map((mentor) => (
              <Grid item xs={12} sm={6} md={3} key={mentor.user_id}>
                <StyledCard>
                  <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                      <Avatar src={imageUrls[mentor.user_id]} alt={`${mentor?.user?.first_name} ${mentor?.user?.last_name ? mentor?.user?.last_name : ''}`} sx={{ width: 56, height: 56 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: '#1F2937',
                            mb: 0.5,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {`${mentor?.user?.first_name} ${mentor?.user?.last_name ? mentor?.user?.last_name : ''}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1 }}>
                          {mentor.degree}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: 16, color: '#FFA500' }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1F2937' }}>
                              {mentor?.rating}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>
                            {mentor?.review} Reviews
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#6B7280' }}>
                            {mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === mentor.experience_years)?.label} Exp` : '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      {mentor?.expertise?.map((tag, index) => (
                        <StyledChip key={index} label={tag} size="small" />
                      ))}
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6B7280',
                        mb: 2,
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {mentor.bio}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <ViewProfileButton variant="outlined" fullWidth onClick={() => handleViewProfile(mentor)}>
                        View Profile
                      </ViewProfileButton>
                      {details.type === 'counsellor' ? (
                        <BookSessionButton variant="contained" fullWidth onClick={() => {
                          setOpen(true);
                          setSelectedMentor(mentor);
                          setBookingUrl(mentor?.calendly_meeting_link);
                        }}>
                          Book Session
                        </BookSessionButton>
                      ) :
                        <BookSessionButton
                          variant="contained"
                          fullWidth
                          onClick={async () => {
                            try {
                              dispatch(setLoading({
                                loading: true
                              }));
                              const response = await inviteCounsellor({
                                'mentor_profile_id': mentor?.mentor_profile_id
                              });
                              if (response.success) {
                                showNotification(response.message, 'success');
                                dispatch(setLoading({
                                  loading: false
                                }));
                              }
                            } catch (err) {
                              const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                              showNotification(errorMessage, 'error');
                              dispatch(setLoading({
                                loading: false
                              }));
                            }
                          }}
                        >
                          Invite A Counselor
                        </BookSessionButton>}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </Box>) : (
        <Typography textAlign="center" color={theme.palette.background.dark}>No Data Found</Typography>
      )}

      {/* Pagination */}
      {totalPages > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 3,
            borderTop: '1px solid #E5E7EB',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Showing {startItem}-{endItem} from {mentors?.length} data
          </Typography>
          <StyledPagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            shape="rounded"
            siblingCount={1}
            boundaryCount={1}
          />
        </Box>
      )}

      <StyledDialog open={viewProfileDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, position: 'relative' }}>
          <IconButton
            onClick={handleCloseAll}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#6B7280',
            }}
          >
            <CloseIcon />
          </IconButton>

          {selectedMentor && (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                <Avatar src={imageUrls[selectedMentor.user_id]} sx={{ width: 120, height: 120, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {`${selectedMentor?.user?.first_name} ${selectedMentor?.user?.last_name ? selectedMentor?.user?.last_name : ''}`}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                  {selectedMentor.degree}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon sx={{ fontSize: 20, color: '#FFA500' }} />
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                   {selectedMentor?.rating}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    {selectedMentor?.review} Reviews
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280' }}>
                    • {selectedMentor.experience_years ? `${ExpierienceYears.find((a) => a.value === selectedMentor.experience_years)?.label} Exp` : '-'}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                  About
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                  {selectedMentor.bio}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                  Expertise
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedMentor?.expertise?.map((tag, index) => (
                    <StyledChip key={index} label={tag} />
                  ))}
                </Box>
              </Box>

              {selectedMentor?.experiences?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Experience
                  </Typography>
                  {selectedMentor?.experiences?.map((exp, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          backgroundColor: '#F3F4F6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <img src='/Images/viewProfile.png' alt='ViewProfile' />
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {exp.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                          {exp.company} -  {formatDateRange(exp.startDate, exp.endDate, exp.currentJob)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseAll}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '8px',
                    height: '40px',
                    borderColor: '#D1D5DB',
                    color: '#374151',
                  }}
                >
                  Cancel
                </Button>
                {details.type === 'counsellor' ? (
                  <BookSessionButton
                    fullWidth
                    onClick={() => {
                      setOpen(true);
                      setSelectedMentor(selectedMentor);
                      setBookingUrl(selectedMentor?.calendly_meeting_link);
                    }}
                    sx={{ height: '40px' }}
                  >
                    Book A Session
                  </BookSessionButton>
                ) :
                  <BookSessionButton
                    variant="contained"
                    fullWidth
                    onClick={async () => {
                      try {
                        dispatch(setLoading({
                          loading: true
                        }));
                        const response = await inviteCounsellor({
                          'mentor_profile_id': selectedMentor?.mentor_profile_id
                        });
                        if (response.success) {
                          showNotification(response.message, 'success');
                          dispatch(setLoading({
                            loading: false
                          }));
                        }
                      } catch (err) {
                        const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                        showNotification(errorMessage, 'error');
                        dispatch(setLoading({
                          loading: false
                        }));
                      }
                    }}
                  >
                    Invite A Counselor
                  </BookSessionButton>}
              </Box>
            </>
          )}
        </DialogContent>
      </StyledDialog>

      <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, position: 'relative' }}>
          <IconButton
            onClick={handleCloseAll}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#6B7280',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Confirm Your Session
          </Typography>

          {selectedMentor && (
            <>
              <Box
                sx={{
                  p: 2,
                  border: '1px solid #E5E7EB',
                  borderRadius: '12px',
                  display: 'flex',
                  gap: 2,
                  mb: 3,
                }}
              >
                <Avatar src={imageUrls[selectedMentor.user_id]} sx={{ width: 60, height: 60 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {`${selectedMentor?.user?.first_name} ${selectedMentor?.user?.last_name ? selectedMentor?.user?.last_name : ''}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                    {selectedMentor.degree}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ fontSize: 16, color: '#FFA500' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {selectedMentor?.rating}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {selectedMentor?.review} Reviews
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      • {selectedMentor.experience_years ? `${ExpierienceYears.find((a) => a.value === selectedMentor.experience_years)?.label} Exp` : '-'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <img
                      src='/Images/graycalendar.png'
                      style={{
                        width: 20,
                        position: 'relative'
                      }}
                      alt=''
                    />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        Date
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: '#F9FAFB',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B7280' }}>
                        Time
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* <Box
                sx={{
                  p: 2,
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  mb: 3,
                }}
              >
                <img
                  src='/Images/Calendar.png'
                  style={{
                    width: 20,
                    position: 'relative'
                  }}
                  alt=''
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Project Demo & Architecture
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Topic
                  </Typography>
                </Box>
              </Box> */}

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                  Any specific question for this session
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Write here"
                  value={sessionNote}
                  onChange={(e) => setSessionNote(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    },
                  }}
                />
              </Box>

              <Button
                fullWidth
                onClick={handleConfirmBooking}
                sx={{
                  backgroundColor: '#1E3A8A',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: '48px',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#1E40AF',
                  },
                }}
              >
                Confirm Booking
              </Button>
            </>
          )}
        </DialogContent>
      </StyledDialog>

      <StyledDialog open={confirmationDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, position: 'relative' }}>
          <IconButton
            onClick={handleCloseAll}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#6B7280',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
            <img
              src='/Images/success.png'
              alt='Check Circle'
              style={{ width: '70px', height: '70px', marginTop: 20, marginBottom: 10 }}
            />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Booking Confirmed
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', mb: 4 }}>
              Booking Confirmed! Check your email for the meeting link.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 4, width: '100%' }}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <img
                    src='/Images/graycalendar.png'
                    style={{
                      width: 20,
                      position: 'relative'
                    }}
                    alt=''
                  />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Date
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: '#F9FAFB',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Time
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <BookSessionButton fullWidth sx={{ height: '40px' }} onClick={() => {
                navigate(`/dashboard`);
                setEventData(null);
              }}>
                Go To Dashboard
              </BookSessionButton>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleCloseAll}
                sx={{
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: '40px',
                  borderColor: '#D1D5DB',
                  color: '#374151',
                }}
              >
                I'll Do It Later
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </StyledDialog>
    </Box>
  );
};

export default MentorListing;